import { z } from 'zod';
import { boardParams, description, endpoint, id, isoDate, LIMITS, seriesScope, title } from './common';

// Tasks: creating and changing them, archiving, searching, and recurring series.

// A task as the API lists it.
export const task = z.object({
    id: z.string(),
    boardId: z.string(),
    sectionId: z.string(),
    title: z.string(),
    description: z.string().optional(),
    date: z.string().nullable().optional(),
    isCompleted: z.boolean().optional(),
    completedAt: z.string().optional(),
    completedBy: z.string().optional(),
    seriesId: z.string().optional(),
    createdAt: z.string().optional(),
    // Set on a task made from an event of a connected Google or Apple calendar (see calendar.ts).
    calendarFeedId: z.string().optional(),
}).passthrough();
export type Task = z.infer<typeof task>;

export const archivedTask = z.object({
    id: z.string(),
    title: z.string(),
    boardId: z.string(),
    sectionId: z.string(),
    date: z.string().nullable().optional(),
    isCompleted: z.boolean().optional(),
    completedAt: z.string().optional(),
    archivedAt: z.string(),
}).passthrough();
export type ArchivedTask = z.infer<typeof archivedTask>;

export const taskSearchResult = z.object({
    id: z.string(),
    title: z.string(),
    date: z.string().nullable().optional(),
    sectionId: z.string(),
    seriesId: z.string().optional(),
    isCompleted: z.boolean().optional(),
    occurrences: z.number(),            // tasks in this result's recurring series that matched (1 if standalone)
});
export type TaskSearchResult = z.infer<typeof taskSearchResult>;

export const getSectionTasks = endpoint({
    method: 'GET',
    path: '/boards/:boardId/tasks',
    params: boardParams,
    response: z.object({ sectionTasks: z.array(task) }),
});

export const createSectionTask = endpoint({
    method: 'POST',
    path: '/createSectionTask',
    body: z.object({
        boardId: id,
        sectionId: id,
        taskTitle: title('The task title'),
        taskDescription: description.default(''),
        taskDate: isoDate.optional(),
    }),
    response: z.object({ taskId: id }),
});

// What can be changed on a task. `date: null` clears the date. `sectionId` moves the task to another section of the same board
// (to the bottom of it).
export const taskChanges = z.object({
    title: title('The task title').optional(),
    description: description.optional(),
    sectionId: id.optional(),
    date: isoDate.nullable().optional(),
    isCompleted: z.boolean().optional(),
});
export type TaskChanges = z.infer<typeof taskChanges>;

export const updateSectionTask = endpoint({
    method: 'POST',
    path: '/updateSectionTask',
    body: z.object({ taskId: id, updatedAttributes: taskChanges }),
    response: z.object({ taskId: id }),
});

export const deleteSectionTask = endpoint({
    method: 'POST',
    path: '/deleteSectionTask',
    body: z.object({ boardId: id, sectionId: id, taskId: id }),
    response: z.object({ taskId: id }),
});

export const setTaskArchived = endpoint({
    method: 'POST',
    path: '/setTaskArchived',
    body: z.object({ taskId: id, archived: z.boolean({ required_error: 'archived must be true or false', invalid_type_error: 'archived must be true or false' }) }),
    response: z.object({ taskId: id, archived: z.boolean() }),
});

export const getArchivedTasks = endpoint({
    method: 'GET',
    path: '/boards/:boardId/tasks/archived',
    params: boardParams,
    response: z.object({ archivedTasks: z.array(archivedTask) }),
});

export const searchTasks = endpoint({
    method: 'GET',
    path: '/boards/:boardId/tasks/search',
    params: boardParams,
    query: z.object({
        q: z.string().trim().min(1, `q must be 1 to ${LIMITS.searchQuery} characters`).max(LIMITS.searchQuery, `q must be 1 to ${LIMITS.searchQuery} characters`),
    }),
    response: z.object({ results: z.array(taskSearchResult) }),
});

// --- recurring series ------------------------------------------------------------------------------------------

export const createRecurringTasks = endpoint({
    method: 'POST',
    path: '/createRecurringTasks',
    body: z.object({
        boardId: id,
        sectionId: id,
        taskTitle: title('The task title'),
        taskDescription: description.default(''),
        dates: z.array(isoDate).min(1).max(LIMITS.recurringOccurrences, `dates must be 1 to ${LIMITS.recurringOccurrences} valid date strings`),
    }),
    response: z.object({ seriesId: id, taskIds: z.array(z.string()) }),
});

export const updateTaskSeries = endpoint({
    method: 'POST',
    path: '/updateTaskSeries',
    body: z.object({
        taskId: id,
        scope: seriesScope,
        title: title('The title').optional(),
        description: description.optional(),
        sectionId: id.optional(),
        shiftMinutes: z.number().int().min(-LIMITS.seriesShiftMinutes).max(LIMITS.seriesShiftMinutes, `shiftMinutes must be a whole number within ±${LIMITS.seriesShiftMinutes}`).optional(),
    }).refine((body) => body.title !== undefined || body.description !== undefined || body.sectionId !== undefined || body.shiftMinutes !== undefined,
        { message: 'Provide something to change: a title, description, section and/or shiftMinutes' }),
    response: z.object({ updatedCount: z.number() }),
});

export const deleteTaskSeries = endpoint({
    method: 'POST',
    path: '/deleteTaskSeries',
    body: z.object({ taskId: id, scope: seriesScope }),
    response: z.object({ deletedTaskIds: z.array(z.string()) }),
});
